import { useEffect, useState } from "react";
import API_BASE_URL, { authFetch } from "../services/api";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
} from "recharts";

function StatisticsPage() {
  const [selectedSector, setSelectedSector] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [quickPeriod, setQuickPeriod] = useState("last_12_months");

  const [selectedCustomerType, setSelectedCustomerType] = useState("");
  const [selectedTechnician, setSelectedTechnician] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedActivityType, setSelectedActivityType] = useState("");

  const [filterOptions, setFilterOptions] = useState({
    sectors: [],
    customer_types: [],
    technicians: [],
    cities: [],
    activity_types: [],
  });

  const [kpi, setKpi] = useState({
    interventions: 0,
    plants: 0,
    customers: 0,
    deadlines: 0,
  });

  const [plantsBySector, setPlantsBySector] = useState([]);
  const [plantsByCategory, setPlantsByCategory] = useState([]);
  const [plantsByCity, setPlantsByCity] = useState([]);
  const [plantsByBrand, setPlantsByBrand] = useState([]);
  const [customersByType, setCustomersByType] = useState([]);

  const [interventionsByMonth, setInterventionsByMonth] = useState([]);
  const [deadlinesByMonth, setDeadlinesByMonth] = useState([]);
  const [interventionsByTechnician, setInterventionsByTechnician] = useState([]);
  const [interventionsByType, setInterventionsByType] = useState([]);
  const [technicianProductivity, setTechnicianProductivity] = useState([]);

  const [topCustomersByPlants, setTopCustomersByPlants] = useState([]);
  const [topCustomersByInterventions, setTopCustomersByInterventions] =
    useState([]);

  const [upcomingDeadlines, setUpcomingDeadlines] = useState({
    within_30: 0,
    within_60: 0,
    within_90: 0,
  });

  const CHART_COLORS = [
    "#173a63",
    "#2563eb",
    "#0ea5e9",
    "#14b8a6",
    "#10b981",
    "#84cc16",
    "#f59e0b",
    "#f97316",
    "#ef4444",
    "#ec4899",
    "#8b5cf6",
    "#6366f1",
  ];

  useEffect(() => {
    setLast12MonthsPeriod();
    loadFilterOptions();
  }, []);

  useEffect(() => {
    loadAllStatistics();
  }, [
    selectedSector,
    dateFrom,
    dateTo,
    selectedCustomerType,
    selectedTechnician,
    selectedCity,
    selectedActivityType,
  ]);

  function buildStatsQueryParams(includeInterventionFilters = true) {
    const params = new URLSearchParams();

    if (selectedSector) params.append("sector", selectedSector);
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    if (selectedCustomerType) params.append("customer_type", selectedCustomerType);
    if (selectedCity) params.append("city", selectedCity);

    if (includeInterventionFilters) {
      if (selectedTechnician) params.append("technician_name", selectedTechnician);
      if (selectedActivityType) params.append("activity_type", selectedActivityType);
    }

    return params.toString();
  }

  function formatDate(date) {
    return date.toISOString().split("T")[0];
  }

  function setLast12MonthsPeriod() {
    const today = new Date();
    const from = new Date();

    from.setMonth(today.getMonth() - 11);
    from.setDate(1);

    setQuickPeriod("last_12_months");
    setDateFrom(formatDate(from));
    setDateTo(formatDate(today));
  }

  function handleQuickPeriodChange(value) {
    setQuickPeriod(value);

    if (value === "custom") return;

    if (value === "all") {
      setDateFrom("");
      setDateTo("");
      return;
    }

    const today = new Date();
    const from = new Date();

    if (value === "last_month") from.setMonth(today.getMonth() - 1);
    if (value === "last_3_months") from.setMonth(today.getMonth() - 3);
    if (value === "last_6_months") from.setMonth(today.getMonth() - 6);

    if (value === "last_12_months") {
      from.setMonth(today.getMonth() - 11);
      from.setDate(1);
    }

    if (value === "current_year") {
      from.setMonth(0);
      from.setDate(1);
    }

    setDateFrom(formatDate(from));
    setDateTo(formatDate(today));
  }

  function clearFilters() {
    setSelectedSector("");
    setSelectedCustomerType("");
    setSelectedTechnician("");
    setSelectedCity("");
    setSelectedActivityType("");
    setLast12MonthsPeriod();
  }

  async function loadAllStatistics() {
    await Promise.all([
      loadKpi(),
      loadPlantsBySector(),
      loadPlantsByCategory(),
      loadPlantsByCity(),
      loadPlantsByBrand(),
      loadCustomersByType(),
      loadInterventionsByMonth(),
      loadDeadlinesByMonth(),
      loadInterventionsByTechnician(),
      loadInterventionsByType(),
      loadTechnicianProductivity(),
      loadTopCustomersByPlants(),
      loadTopCustomersByInterventions(),
      loadUpcomingDeadlines(),
    ]);
  }

  async function loadFilterOptions() {
    try {
      const response = await authFetch(`${API_BASE_URL}/statistics/filter-options`);
      const data = await response.json();
      setFilterOptions(data);
    } catch (error) {
      console.error(error);
    }
  }

  async function loadKpi() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/kpi?${buildStatsQueryParams()}`
      );
      const data = await response.json();

      setKpi({
        interventions: data.interventions || 0,
        plants: data.plants || 0,
        customers: data.customers || 0,
        deadlines: data.deadlines || 0,
      });
    } catch (error) {
      console.error(error);
    }
  }

  async function loadPlantsBySector() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/plants-by-sector?${buildStatsQueryParams(false)}`
      );
      const data = await response.json();
      setPlantsBySector(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  }

  async function loadPlantsByCategory() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/plants-by-category?${buildStatsQueryParams(false)}`
      );
      const data = await response.json();
      setPlantsByCategory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  }

  async function loadPlantsByCity() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/plants-by-city?${buildStatsQueryParams(false)}`
      );
      const data = await response.json();
      setPlantsByCity(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  }

  async function loadPlantsByBrand() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/plants-by-brand?${buildStatsQueryParams(false)}`
      );
      const data = await response.json();
      setPlantsByBrand(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  }

  async function loadCustomersByType() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/customers-by-type?${buildStatsQueryParams(false)}`
      );
      const data = await response.json();
      setCustomersByType(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  }

  async function loadInterventionsByMonth() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/interventions-by-month?${buildStatsQueryParams()}`
      );
      const data = await response.json();
      setInterventionsByMonth(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  }

  async function loadDeadlinesByMonth() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/deadlines-by-month?${buildStatsQueryParams(false)}`
      );
      const data = await response.json();
      setDeadlinesByMonth(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  }

  async function loadInterventionsByTechnician() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/interventions-by-technician?${buildStatsQueryParams()}`
      );
      const data = await response.json();
      setInterventionsByTechnician(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  }

  async function loadInterventionsByType() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/interventions-by-type?${buildStatsQueryParams()}`
      );
      const data = await response.json();
      setInterventionsByType(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  }

  async function loadTechnicianProductivity() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/technician-productivity?${buildStatsQueryParams()}`
      );
      const data = await response.json();
      setTechnicianProductivity(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  }

  async function loadTopCustomersByPlants() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/top-customers-by-plants?${buildStatsQueryParams(false)}`
      );
      const data = await response.json();
      setTopCustomersByPlants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  }

  async function loadTopCustomersByInterventions() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/top-customers-by-interventions?${buildStatsQueryParams()}`
      );
      const data = await response.json();
      setTopCustomersByInterventions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  }

  async function loadUpcomingDeadlines() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/statistics/upcoming-deadlines?${buildStatsQueryParams(false)}`
      );
      const data = await response.json();

      setUpcomingDeadlines({
        within_30: data.within_30 || 0,
        within_60: data.within_60 || 0,
        within_90: data.within_90 || 0,
      });
    } catch (error) {
      console.error(error);
    }
  }

  const selectStyle = {
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    background: "#f8fafc",
  };

  const cardStyle = {
    background: "#ffffff",
    border: "1px solid #dbe4f0",
    borderRadius: "16px",
    padding: "22px",
  };

  return (
    <div style={{ padding: "28px" }}>

  <h1
  style={{
    color: "#173a63",
    fontSize: "36px",
    fontWeight: "400",
    marginBottom: "12px",
  }}
>
  Statistiche
</h1>

  <p
  style={{
    color: "#475569",
    fontSize: "15px",
    lineHeight: "1.7",
    maxWidth: "1100px",
    marginBottom: "18px",
  }}
>
  Questa sezione consente di analizzare l'andamento operativo
  dell'azienda attraverso indicatori, grafici e statistiche
  aggiornate in tempo reale.
  Utilizza i filtri disponibili per limitare l'analisi ad uno
  specifico periodo, settore, tecnico, tipologia cliente,
  città o attività.
</p>

<div
  style={{
    background: "#f8fafc",
    border: "1px solid #dbe4f0",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "24px",
  }}
>
  <strong style={{ color: "#173a63" }}>
    Guida rapida alla pagina
  </strong>

  <ul
    style={{
      marginTop: "10px",
      marginBottom: 0,
      color: "#475569",
      lineHeight: "1.8",
      paddingLeft: "20px",
    }}
  >
    <li>
      Analizza KPI aziendali relativi a clienti, impianti,
      interventi e scadenze tecniche.
    </li>

    <li>
      Filtra i dati per periodo, settore, tecnico,
      tipologia cliente, città e attività.
    </li>

    <li>
      Monitora la produttività dei tecnici e la distribuzione
      degli impianti sul territorio.
    </li>

    <li>
      Individua rapidamente i clienti più attivi,
      i tecnici più operativi e le principali aree
      di intervento dell'azienda.
    </li>

    <li>
      Analizza l'andamento delle scadenze tecniche,
      la conformità degli impianti e l'evoluzione
      delle attività nel periodo selezionato.
    </li>

    <li>
      Utilizza il pulsante <strong>"Pulisci filtri"</strong>
      per ripristinare rapidamente la visualizzazione
      completa dei dati aziendali.
    </li>
  </ul>
</div>

  <div style={{ ...cardStyle, marginBottom: "24px" }}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px",
      }}
    >
          <h2 style={{ color: "#173a63", margin: 0 }}>Filtri</h2>

          <button
            onClick={clearFilters}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#173a63";
              e.currentTarget.style.color = "#ffffff";
              e.currentTarget.style.borderColor = "#173a63";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#ffffff";
              e.currentTarget.style.color = "#173a63";
              e.currentTarget.style.borderColor = "#cbd5e1";
              e.currentTarget.style.transform = "translateY(0)";
            }}
            style={{
              padding: "10px 14px",
              borderRadius: "10px",
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              cursor: "pointer",
              fontWeight: 700,
              color: "#173a63",
              transition: "all 0.2s ease",
            }}
          >
            Pulisci filtri
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "14px",
          }}
        >
          <select
            value={quickPeriod}
            onChange={(e) => handleQuickPeriodChange(e.target.value)}
            style={selectStyle}
          >
            <option value="last_12_months">Periodo: ultimi 12 mesi</option>
            <option value="last_month">Ultimo mese</option>
            <option value="last_3_months">Ultimi 3 mesi</option>
            <option value="last_6_months">Ultimi 6 mesi</option>
            <option value="current_year">Anno corrente</option>
            <option value="all">Tutto lo storico</option>
            <option value="custom">Periodo personalizzato</option>
          </select>

          <div>
            <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b" }}>
              Dal
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setQuickPeriod("custom");
                setDateFrom(e.target.value);
              }}
              style={{
                ...selectStyle,
                width: "100%",
                marginTop: "4px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div>
            <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b" }}>
              Al
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setQuickPeriod("custom");
                setDateTo(e.target.value);
              }}
              style={{
                ...selectStyle,
                width: "100%",
                marginTop: "4px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <select
            value={selectedSector}
            onChange={(e) => setSelectedSector(e.target.value)}
            style={selectStyle}
          >
            <option value="">Settore: Tutti</option>
            {filterOptions.sectors.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          <select
            value={selectedCustomerType}
            onChange={(e) => setSelectedCustomerType(e.target.value)}
            style={selectStyle}
          >
            <option value="">Tipo cliente: Tutti</option>
            {filterOptions.customer_types.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          <select
            value={selectedTechnician}
            onChange={(e) => setSelectedTechnician(e.target.value)}
            style={selectStyle}
          >
            <option value="">Tecnico: Tutti</option>
            {filterOptions.technicians.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          <select
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            style={selectStyle}
          >
            <option value="">Città / Provincia: Tutti</option>
            {filterOptions.cities.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          <select
            value={selectedActivityType}
            onChange={(e) => setSelectedActivityType(e.target.value)}
            style={selectStyle}
          >
            <option value="">Tipo attività: Tutti</option>
            {filterOptions.activity_types.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "18px",
          marginBottom: "24px",
        }}
      >
        {[
          { label: "Interventi", value: kpi.interventions },
          { label: "Impianti", value: kpi.plants },
          { label: "Clienti", value: kpi.customers },
          { label: "Scadenze", value: kpi.deadlines },
        ].map((item) => (
          <div key={item.label} style={{ ...cardStyle, padding: "20px" }}>
            <div style={{ color: "#64748b", fontWeight: 600 }}>{item.label}</div>
            <div
              style={{
                fontSize: "34px",
                fontWeight: 800,
                color: "#173a63",
                marginTop: "8px",
              }}
            >
              {item.value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "18px",
        }}
      >
        <ChartCard title="Panoramica operativa">
          <BarChart data={interventionsByMonth}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Bar
              dataKey="count"
              name="Interventi"
              fill="#173a63"
              radius={[6, 6, 0, 0]}
            />
          </BarChart>
        </ChartCard>

        <ChartCard title="Scadenze e conformità">
          <BarChart data={deadlinesByMonth}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Bar
              dataKey="count"
              name="Scadenze"
              fill="#dc2626"
              radius={[6, 6, 0, 0]}
            />
          </BarChart>
        </ChartCard>

        <PieCard
          title="Impianti e settori"
          data={plantsBySector}
          nameKey="sector"
          colors={CHART_COLORS}
        />

        <PieCard
          title="Impianti per categoria"
          data={plantsByCategory}
          nameKey="category"
          colors={CHART_COLORS}
        />

        <PieCard
          title="Clienti"
          data={customersByType}
          nameKey="type"
          colors={CHART_COLORS}
        />

        <HorizontalBarCard
          title="Interventi per tecnico"
          data={interventionsByTechnician}
          yKey="technician"
          barKey="count"
          barName="Interventi"
          colors={CHART_COLORS}
        />

        <HorizontalBarCard
          title="Interventi per tipo attività"
          data={interventionsByType}
          yKey="type"
          barKey="count"
          barName="Interventi"
          colors={CHART_COLORS}
          yWidth={190}
        />

        <HorizontalDoubleBarCard
          title="Produttività tecnici"
          data={technicianProductivity}
          yKey="technician"
          firstKey="total"
          firstName="Totale interventi"
          firstColor="#2563eb"
          secondKey="completed"
          secondName="Completati"
          secondColor="#16a34a"
        />

        <HorizontalBarCard
          title="Distribuzione impianti per città"
          data={plantsByCity}
          yKey="city"
          barKey="count"
          barName="Impianti"
          colors={CHART_COLORS}
        />

        <HorizontalBarCard
          title="Distribuzione impianti per marca"
          data={plantsByBrand}
          yKey="brand"
          barKey="count"
          barName="Impianti"
          colors={CHART_COLORS}
        />

        <ProfessionalTopCard
          title="Top clienti per impianti"
          data={topCustomersByPlants}
          valueLabel="impianti"
          color="#10b981"
        />

        <ProfessionalTopCard
          title="Top clienti per interventi"
          data={topCustomersByInterventions}
          valueLabel="interventi"
          color="#f59e0b"
        />
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #dbe4f0",
        borderRadius: "16px",
        padding: "22px",
      }}
    >
      <h2 style={{ color: "#173a63", marginBottom: "14px" }}>{title}</h2>
      <div style={{ height: "300px" }}>
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function HorizontalBarCard({
  title,
  data,
  yKey,
  barKey,
  barName,
  colors,
  yWidth = 150,
}) {
  const rowHeight = 48;
  const maxVisibleRows = 7;
  const chartHeight = Math.max(360, data.length * rowHeight);
  const visibleHeight = Math.min(chartHeight, maxVisibleRows * rowHeight);

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #dbe4f0",
        borderRadius: "16px",
        padding: "22px",
      }}
    >
      <h2 style={{ color: "#173a63", marginBottom: "14px" }}>{title}</h2>

      {data.length === 0 ? (
        <p style={{ color: "#64748b" }}>Nessun dato disponibile.</p>
      ) : (
        <div
  style={{
    height: `${visibleHeight}px`,
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: "8px",
  }}
>
          <div style={{ height: `${chartHeight}px` }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
  type="number"
  allowDecimals={false}
  domain={[0, (dataMax) => Math.max(dataMax + 1, 4)]}
/>
                <YAxis type="category" dataKey={yKey} width={yWidth} />
                <Tooltip />
                <Bar
                  dataKey={barKey}
                  name={barName}
                  radius={[0, 6, 6, 0]}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`${barKey}-${index}`}
                      fill={colors[index % colors.length]}
                    />
                  ))}

                  <LabelList
                    dataKey={barKey}
                    position="right"
                    style={{
                      fill: "#0f172a",
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function HorizontalDoubleBarCard({
  title,
  data,
  yKey,
  firstKey,
  firstName,
  firstColor,
  secondKey,
  secondName,
  secondColor,
  yWidth = 150,
}) {
  const rowHeight = 58;
  const maxVisibleRows = 6;
  const chartHeight = Math.max(360, data.length * rowHeight);
  const visibleHeight = Math.min(chartHeight, maxVisibleRows * rowHeight);

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #dbe4f0",
        borderRadius: "16px",
        padding: "22px",
      }}
    >
      <h2 style={{ color: "#173a63", marginBottom: "14px" }}>{title}</h2>

      {data.length === 0 ? (
        <p style={{ color: "#64748b" }}>Nessun dato disponibile.</p>
      ) : (
        <div
          style={{
            height: `${visibleHeight}px`,
            overflowY: "auto",
            paddingRight: "8px",
          }}
        >
          <div style={{ height: `${chartHeight}px` }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={data}>
                <CartesianGrid strokeDasharray="3 3" />

<XAxis
  type="number"
  allowDecimals={false}
  domain={[0, (dataMax) => Math.max(dataMax + 1, 4)]}
/>

<YAxis
  type="category"
  dataKey={yKey}
  width={yWidth}
/>

<Tooltip />

<Legend
  verticalAlign="top"
  align="right"
  height={36}
/>

<Bar
  dataKey={firstKey}
  name={firstName}
  fill={firstColor}
  radius={[0, 6, 6, 0]}
>
  <LabelList
    dataKey={firstKey}
    position="right"
    style={{
      fill: "#0f172a",
      fontWeight: 700,
      fontSize: 12,
    }}
  />
</Bar>
                <Bar
                  dataKey={secondKey}
                  name={secondName}
                  fill={secondColor}
                  radius={[0, 6, 6, 0]}
                >
                  <LabelList
                    dataKey={secondKey}
                    position="right"
                    style={{
                      fill: "#0f172a",
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function PieCard({ title, data, nameKey, colors }) {
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #dbe4f0",
        borderRadius: "16px",
        padding: "22px",
      }}
    >
      <h2 style={{ color: "#173a63", marginBottom: "14px" }}>{title}</h2>
      <div style={{ height: "280px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="count" nameKey={nameKey} outerRadius={90}>
              {data.map((entry, index) => (
                <Cell key={index} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ProfessionalTopCard({ title, data, valueLabel, color }) {
  const maxValue = Math.max(...data.map((item) => item.count || 0), 1);

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #dbe4f0",
        borderRadius: "16px",
        padding: "22px",
      }}
    >
      <h2 style={{ color: "#173a63", marginBottom: "16px" }}>{title}</h2>

      {data.length === 0 ? (
        <p style={{ color: "#64748b" }}>Nessun dato disponibile.</p>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            maxHeight: "300px",
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: "8px",
          }}
        >
          {data.map((item, index) => {
            const percentage = ((item.count || 0) / maxValue) * 100;

            return (
              <div key={`${item.customer}-${index}`}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginBottom: "4px",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: "#173a63" }}>
                      {index + 1}. {item.customer}
                    </div>

                    <div style={{ fontSize: "12px", color: "#64748b" }}>
                      {item.customer_type || "Non indicato"}
                    </div>
                  </div>

                  <div
                    style={{
                      fontWeight: 800,
                      color: "#0f172a",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.count} {valueLabel}
                  </div>
                </div>

                <div
                  style={{
                    height: "8px",
                    background: "#e2e8f0",
                    borderRadius: "999px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${percentage}%`,
                      height: "100%",
                      background: color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default StatisticsPage;
